import React from "react";
import { Box, Tooltip } from "@mui/material";
import { MetricUnit, formatMetric } from "./useMetrics";

const UP = "#2e7d32";
const DOWN = "#c62828";
const FLAT = "#8a8a93";

export interface DeltaChipProps {
  current: number;
  /** Previous-period value; `null`/`undefined` renders nothing. */
  previous: number | null | undefined;
  unit?: MetricUnit;
  /** Label of the previous period, shown in the tooltip. */
  periodLabel?: string;
  size?: "small" | "medium";
}

export interface DeltaParts {
  direction: "up" | "down" | "flat" | "new";
  /** Percent change, or null when the previous period was zero. */
  percent: number | null;
  diff: number;
}

export const deltaParts = (current: number, previous: number): DeltaParts => {
  const diff = current - previous;
  if (previous === 0) {
    if (current === 0) return { direction: "flat", percent: 0, diff: 0 };
    return { direction: "new", percent: null, diff };
  }
  const percent = (diff / Math.abs(previous)) * 100;
  if (diff === 0) return { direction: "flat", percent: 0, diff: 0 };
  return { direction: diff > 0 ? "up" : "down", percent, diff };
};

const formatPercent = (percent: number) => {
  const abs = Math.abs(percent);
  const digits = abs > 0 && abs < 10 ? 1 : 0;
  return `${abs.toFixed(digits)}%`;
};

/**
 * Up/down chip comparing a metric against the previous period. Every metric in
 * this hub reads better when it grows, so up is always green and down red.
 */
const DeltaChip: React.FC<DeltaChipProps> = ({
  current,
  previous,
  unit,
  periodLabel,
  size = "small",
}) => {
  if (previous === null || previous === undefined) return null;

  const { direction, percent, diff } = deltaParts(current, previous);
  const color =
    direction === "up" || direction === "new"
      ? UP
      : direction === "down"
        ? DOWN
        : FLAT;
  const arrow =
    direction === "up" || direction === "new"
      ? "▲"
      : direction === "down"
        ? "▼"
        : "—";
  const text =
    direction === "new"
      ? "new"
      : direction === "flat"
        ? "0%"
        : percent === null
          ? ""
          : formatPercent(percent);

  const tip = `Previous period${periodLabel ? ` (${periodLabel})` : ""}: ${formatMetric(
    previous,
    unit,
  )} · change ${diff >= 0 ? "+" : "−"}${formatMetric(Math.abs(diff), unit)}`;

  return (
    <Tooltip title={tip} placement="top">
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "2px",
          px: 0.6,
          py: "1px",
          borderRadius: "10px",
          backgroundColor: `${color}14`,
          color,
          fontWeight: 700,
          fontSize: size === "small" ? "0.68rem" : "0.78rem",
          lineHeight: 1.4,
          whiteSpace: "nowrap",
        }}
      >
        <span style={{ fontSize: "0.8em" }}>{arrow}</span>
        {text}
      </Box>
    </Tooltip>
  );
};

export default DeltaChip;
